import React, { useState, useEffect, useRef } from 'react';
import { MapPin, Clock, Users, Globe } from 'lucide-react';
import { whyChooseUs } from '../../data/mock';

const iconMap = {
  0: MapPin,
  1: Clock,
  2: Users,
  3: Globe,
};

const AnimatedCounter = ({ end, suffix = '', isVisible }) => {
  const [count, setCount] = useState(0);
  const countRef = useRef(null);

  useEffect(() => {
    if (!isVisible) return;
    
    const numericEnd = parseInt(end.replace(/\D/g, '')) || 0;
    const duration = 2000;
    const steps = 60;
    const increment = numericEnd / steps;
    let current = 0;

    const timer = setInterval(() => {
      current += increment;
      if (current >= numericEnd) {
        setCount(numericEnd);
        clearInterval(timer);
      } else {
        setCount(Math.floor(current));
      }
    }, duration / steps);

    return () => clearInterval(timer);
  }, [end, isVisible]);

  // Handle special cases like "24/7" or "100%"
  if (end.includes('/')) return <span>{end}</span>;
  if (end.includes('%')) return <span>{count}%</span>;
  
  return <span>{count}+</span>;
};

const WhyChooseUs = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section id="why-us" ref={sectionRef} className="relative py-24 lg:py-32 bg-[#050505]">
      {/* Decorative elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-[#00D4FF]/5 rounded-full blur-[150px]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-20">
          <span className="text-[#00D4FF] text-sm font-semibold tracking-wider uppercase mb-4 block">
            Why Choose Us
          </span>
          <h2 className="text-3xl lg:text-5xl font-bold text-white mb-6">
            Why Armed Guard Officer.com?
          </h2>
          <p className="text-gray-400 text-lg leading-relaxed">
            We combine Florida-focused expertise with professional excellence to deliver
            security solutions you can trust.
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {whyChooseUs.map((item, index) => {
            const Icon = iconMap[index];
            return (
              <div
                key={item.id}
                className="group relative bg-black border border-white/10 p-8 text-center hover:border-[#00D4FF]/50 transition-all duration-500"
              >
                {/* Top border accent */}
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#00D4FF] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                
                {/* Icon */}
                <div className="w-12 h-12 flex items-center justify-center mx-auto mb-6 border border-[#00D4FF]/30">
                  <Icon className="w-6 h-6 text-[#00D4FF]" strokeWidth={1.5} />
                </div>

                {/* Stat */}
                <div className="text-4xl lg:text-5xl font-bold text-[#00D4FF] mb-2">
                  <AnimatedCounter end={item.stat} isVisible={isVisible} />
                </div>
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-4">
                  {item.statLabel}
                </p>

                {/* Title & Description */}
                <h3 className="text-white font-semibold mb-2">
                  {item.title}
                </h3>
                <p className="text-gray-500 text-sm">
                  {item.description}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUs;
